import { Box, CircularProgress, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import { PAGE_SIZE } from 'services/constants/env';
import {
	downloadInvoiceCsvRequest,
	downloadInvoicePdfRequest,
	getInvoicesRequest,
	getPaymentLinkRequest,
	getPaymentLinkSuccess,
} from 'redux/reducers/invoices/reducer';
import {
	getDownloadInvoiceCsvLoader,
	getDownloadInvoicePdfLoader,
	getInvoices,
	getInvoicesLoader,
	getPaymentLink,
	getPaymentLinkLoader,
} from 'redux/reducers/invoices/selectors';
import { getMerchantId } from 'redux/reducers/auth/selectors';
import { getPayableRequest } from 'redux/reducers/payments/reducer';
import { getPayableInvoices } from 'redux/reducers/payments/selectors';
import { getClientAccessRequest } from 'redux/reducers/paymentMethods/reducer';
import { getPaymentsBlockedMessage } from 'redux/reducers/paymentMethods/selectors';
import { MakePaymentDialog } from 'components/PaymentMethods/MakePaymentDialog';
import { ClientAccessBanner } from 'components/PaymentMethods/ClientAccess';
import { TInvoice, TInvoicesRequest } from 'redux/reducers/invoices/types';
import { ESortDirection } from 'services/constants/types';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getFiltersFromNavigateParams, getOrderByFromNavigateParams } from 'utils/FilterParams';
import { INVOICES_STATUS } from 'redux/reducers/invoices/constants';
import { api } from 'services/api';
import { downloadReceipt } from 'utils/receipt';
import { notificationContainer } from 'utils/notificationContainer';
import { INVOICES_ACTIONS, buildInvoicesTableStructure } from './invoices-structure';

const DEFAULT_ORDER_BY_FIELD = 'invoiceDate';

export const Invoices: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = navigateParams;
	const merchantId = useSelector(getMerchantId);
	const payable = useSelector(getPayableInvoices);
	// Make payment blocked by the operator: no "Pay with saved method", the message is shown above the table
	const paymentsBlockedMessage = useSelector(getPaymentsBlockedMessage);

	// invoices the payment gateway allows to pay now with a saved payment method (one request per page)
	const payableInvoiceIds = useMemo(
		() =>
			new Set(
				paymentsBlockedMessage
					? []
					: (payable?.invoices ?? [])
							.filter((item) => item.payable && item.methods.length > 0)
							.map((item) => item.invoiceId),
			),
		[payable, paymentsBlockedMessage],
	);
	// receipts of invoices paid online through the payment gateway (invoice id → receipt link)
	const [receipts, setReceipts] = useState<Map<number, string>>(() => new Map());

	const tableStructure = useMemo(
		() =>
			buildInvoicesTableStructure(
				(invoice) => payableInvoiceIds.has(invoice.id),
				(invoice) => receipts.has(invoice.id),
			) as TTableStructure<keyof TInvoice>[],
		[payableInvoiceIds, receipts],
	);

	const invoices = useSelector(getInvoices);

	// one request per page: only invoices that are paid or partly paid can have a receipt
	useEffect(() => {
		const ids = (invoices?.data ?? [])
			.filter((item) => item.invoiceStatus === INVOICES_STATUS.PAID || Number(item.paidAmount) > 0)
			.map((item) => item.id);
		if (!ids.length) {
			setReceipts(new Map());
			return undefined;
		}
		let cancelled = false;
		api.payments
			.getInvoiceReceipts(ids)
			.then((response) => {
				if (!cancelled) {
					setReceipts(new Map(response.items.map((item) => [item.invoiceId, item.receiptUrl])));
				}
			})
			.catch(() => {
				// no receipts: the invoices are still shown, only the "Receipt" action is missing
				if (!cancelled) setReceipts(new Map());
			});
		return () => {
			cancelled = true;
		};
	}, [invoices]);
	const invoicesIsLoading = useSelector(getInvoicesLoader);
	const invoicePdfLoader = useSelector(getDownloadInvoicePdfLoader);
	const invoiceCsvLoader = useSelector(getDownloadInvoiceCsvLoader);
	const paymentLink = useSelector(getPaymentLink);
	const paymentLinkLoader = useSelector(getPaymentLinkLoader);

	const [pendingPaymentInvoiceId, setPendingPaymentInvoiceId] = useState<number | null>(null);
	// the payment page window is opened synchronously on click (popup blockers), the link is set after the response
	const paymentWindowRef = useRef<Window | null>(null);
	const prevPaymentLinkLoader = useRef(false);
	const [makePaymentInvoiceId, setMakePaymentInvoiceId] = useState<number | null>(null);

	useEffect(() => {
		dispatch(getPayableRequest({ silent: true }));
		dispatch(getClientAccessRequest());
	}, [dispatch, merchantId]);

	useEffect(() => {
		const requestBody: TInvoicesRequest = {
			pageNumber: page ? +page : 1,
			pageSize: PAGE_SIZE,
			filters: getFiltersFromNavigateParams(tableStructure, navigateParams),
			orderBy: getOrderByFromNavigateParams(tableStructure, navigateParams, {
				defaultFieldName: DEFAULT_ORDER_BY_FIELD,
				defaultDirection: ESortDirection.DESC,
			}),
			searchString: (navigateParams.search as string) || '',
		};
		dispatch(getInvoicesRequest(requestBody));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	useEffect(() => {
		if (!paymentLink || pendingPaymentInvoiceId === null) return;

		const paymentWindow = paymentWindowRef.current;
		paymentWindowRef.current = null;
		if (paymentWindow && !paymentWindow.closed) {
			paymentWindow.location.href = paymentLink;
		} else {
			window.open(paymentLink, '_blank', 'noopener');
		}
		dispatch(getPaymentLinkSuccess(null));
		setPendingPaymentInvoiceId(null);
	}, [dispatch, paymentLink, pendingPaymentInvoiceId]);

	// the link could not be received: close the window opened on click
	useEffect(() => {
		if (
			prevPaymentLinkLoader.current &&
			!paymentLinkLoader &&
			!paymentLink &&
			pendingPaymentInvoiceId !== null
		) {
			paymentWindowRef.current?.close();
			paymentWindowRef.current = null;
			setPendingPaymentInvoiceId(null);
		}
		prevPaymentLinkLoader.current = paymentLinkLoader;
	}, [paymentLinkLoader, paymentLink, pendingPaymentInvoiceId]);

	const openPaymentWindow = (): Window | null => {
		const paymentWindow = window.open('', '_blank');
		if (!paymentWindow) return null;
		try {
			paymentWindow.opener = null;
			paymentWindow.document.title = 'EAS payment';
			paymentWindow.document.body.textContent = 'Opening the payment page…';
		} catch {
			// the window is still usable for the redirect
		}
		return paymentWindow;
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		const invoice = invoices?.data?.find((item) => item.id === id);
		if (!invoice) return;

		if (typeOfDialog === INVOICES_ACTIONS.DOWNLOAD_PDF && !invoicePdfLoader) {
			dispatch(downloadInvoicePdfRequest({ invoiceId: invoice.id }));
			return;
		}

		if (typeOfDialog === INVOICES_ACTIONS.DOWNLOAD_CSV && !invoiceCsvLoader) {
			dispatch(downloadInvoiceCsvRequest({ invoiceId: invoice.id }));
			return;
		}

		if (typeOfDialog === INVOICES_ACTIONS.PAY_ONLINE) {
			if (pendingPaymentInvoiceId !== null) return;
			paymentWindowRef.current = openPaymentWindow();
			dispatch(getPaymentLinkRequest({ invoiceNumber: invoice.invoiceNumber }));
			setPendingPaymentInvoiceId(id);
			return;
		}

		if (typeOfDialog === INVOICES_ACTIONS.PAY_WITH_SAVED_METHOD) {
			if (paymentsBlockedMessage) {
				notificationContainer(paymentsBlockedMessage, 'info');
				return;
			}
			setMakePaymentInvoiceId(invoice.id);
			return;
		}

		if (typeOfDialog === INVOICES_ACTIONS.DOWNLOAD_RECEIPT) {
			const receiptUrl = receipts.get(invoice.id);
			if (receiptUrl) downloadReceipt(receiptUrl);
		}
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<ClientAccessBanner messages={[paymentsBlockedMessage]} sx={{ mb: 1 }} />
			<EasTableWithFilter<TInvoice>
				tableStructure={tableStructure}
				items={invoices}
				itemsIsLoading={invoicesIsLoading || invoiceCsvLoader || invoicePdfLoader}
				handleAction={handleAction}
				minWidth={1200}
				name="invoice"
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography
						variant="h1"
						color="primary"
						sx={{ fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'center', ml: 1 }}
					>
						Invoices
					</Typography>
					{invoiceCsvLoader || invoicePdfLoader || invoicesIsLoading ? (
						<CircularProgress size={20} />
					) : null}
				</Box>
			</EasTableWithFilter>
			<MakePaymentDialog
				open={makePaymentInvoiceId !== null}
				onClose={() => setMakePaymentInvoiceId(null)}
				preselectedInvoiceId={makePaymentInvoiceId}
			/>
		</Box>
	);
};
